import { request } from '@mairistem/tools';
import React from 'react';
import { toast } from 'react-toastify';

import { postInterrogationPaiementsMfo } from '@/routes';
import { useRegieStore, useRegisseurStore } from '@/zustand';

const INTERVALLE_INTERROGATION = 600000; // 10 minutes

/** Compteurs renvoyés par l'interrogation. Chaque événement n'est compté que par l'appel qui le constate. */
type ReponseInterrogation = {
  recusGeneres?: number;
  nouvellesAnomalies?: number;
  rejetsApresRecu?: number;
};

const pluriel = (nombre: number, singulier: string, plurielForme: string) =>
  nombre > 1 ? plurielForme : singulier;

/**
 * Signale au régisseur ce que l'interrogation vient de constater : reçus générés pour des paiements
 * en ligne, paiements en anomalie (aucun reçu généré), prélèvements rejetés après génération du
 * reçu. Les deux derniers se traitent à la main.
 */
const signaler = (reponse: ReponseInterrogation | undefined) => {
  const recusGeneres = reponse?.recusGeneres ?? 0;
  const nouvellesAnomalies = reponse?.nouvellesAnomalies ?? 0;
  const rejetsApresRecu = reponse?.rejetsApresRecu ?? 0;

  if (recusGeneres > 0) {
    toast(
      `${recusGeneres} ${pluriel(recusGeneres, 'reçu a été généré', 'reçus ont été générés')} pour des paiements en ligne.`,
      { toastId: 'mfo-recus-generes', type: 'success' },
    );
  }

  if (nouvellesAnomalies > 0) {
    toast(
      `${nouvellesAnomalies} ${pluriel(nouvellesAnomalies, 'paiement en ligne est', 'paiements en ligne sont')} à vérifier : montant supérieur au reste à payer ou facture modifiée. Aucun reçu n'a été généré.`,
      { toastId: 'mfo-anomalies', type: 'warning', autoClose: false },
    );
  }

  if (rejetsApresRecu > 0) {
    toast(
      `${rejetsApresRecu} ${pluriel(rejetsApresRecu, 'prélèvement a été rejeté', 'prélèvements ont été rejetés')} après génération du reçu : ouvrir le reçu et utiliser « Rejeter le prélèvement ».`,
      { toastId: 'mfo-rejets-apres-recu', type: 'warning', autoClose: false },
    );
  }
};

/**
 * Hook de déclenchement de l'interrogation des paiements MFO (redescente des paiements en ligne et
 * génération de leurs reçus).
 *
 * MFO n'expose aucun webhook, et un cron côté back n'aurait pas le contexte tenant : c'est donc le
 * front qui déclenche l'interrogation, à l'ouverture de la régie puis toutes les 10 minutes, tant
 * qu'un régisseur est connecté. Un changement de régie ou de régisseur relance le cycle.
 *
 * Les échecs restent silencieux : ce n'est pas une action de l'utilisateur, et un échec ne doit ni
 * l'interrompre ni l'inquiéter. Seuls les événements constatés par l'appel sont signalés, une fois :
 * plusieurs onglets ou régisseurs interrogent en parallèle, et le back ne compte un événement que
 * pour l'appel qui le constate.
 */
const useInterrogationPaiementsMFO = (): void => {
  const identifiantRegie = useRegieStore((state) => state.regie?.identifiant);
  const identifiantRegisseur = useRegisseurStore((state) => state.regisseur?.identifiantRegisseur);

  React.useEffect(() => {
    if (!identifiantRegie || !identifiantRegisseur) {
      return;
    }

    const interroger = () => {
      request
        .post(postInterrogationPaiementsMfo(), {})
        .then((response: any) => signaler(response?.data ?? response))
        .catch(() => {});
    };

    // ⚠️ Premier appel différé, et non immédiat. React exécute les effets des enfants avant ceux
    // des parents : au montage, qui a lieu dans le même rendu que la connexion du régisseur, les
    // intercepteurs X-Regie et X-Regisseur des providers parents ne sont pas encore posés. Un appel
    // immédiat partirait sans eux et serait refusé par le contrôle d'habilitation.
    const premierAppel = setTimeout(interroger, 0);
    const intervalle = setInterval(interroger, INTERVALLE_INTERROGATION);

    return () => {
      clearTimeout(premierAppel);
      clearInterval(intervalle);
    };
  }, [identifiantRegie, identifiantRegisseur]);
};

export default useInterrogationPaiementsMFO;
